import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent, ReactNode, useCallback, useEffect, useState} from 'react';

import {assert} from 'Asserts';
import {MonomerCapGroup} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import MonomerConfigureAttachmentPointsSection from 'molbio/monomers/admin/MonomerConfigureAttachmentPointsSection';
import MonomerStructureAndWeightConfigurationPane from 'molbio/monomers/admin/MonomerStructureAndWeightConfigurationPane';
import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from 'molbio/monomers/admin/util';
import notifications from 'notifications';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_LIGHT} from 'ui-library/Colors';
import {InputErrorMessageStyle, LABEL_STYLES} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';
import ToggleGroup from 'ui-library/ToggleGroup';

import {MonomerCapGroupWithWildcard, WILDCARD_CAP_GROUP_SYMBOL} from '../MonomerConstants';
import {
  BaseMonomerUpdate,
  BaseMonomerValidationFields,
  SharedMonomerValidationErrors,
  SharedMonomerValidationOutput,
} from '../SharedMonomerConstants';

export interface SharedMonomerManualEditPageProps {
  monomerId: string;
  initialName: string;
  initialSmilesInput: string;
  initialCustomMolecularWeight: number | null;
  initialSymbol: string;
  // Must be referentially stable (e.g. wrapped in useCallback) so the initial validation runs only once on mount.
  validate: (inputs: Array<BaseMonomerValidationFields>) => Promise<Array<SharedMonomerValidationOutput>>;
  onSave: (update: BaseMonomerUpdate, editingSmiles: boolean) => Promise<void>;
  // Invoked when the user clicks save; callers may intercept to show a confirmation before calling `save`.
  onSaveRequested?: (args: {didStructureChange: boolean; editingSmiles: boolean; save: () => void}) => void;
  libraryUrl: string;
  pageContainer: ReactNode;
  dataTestComponent: string;
  // When true, all inputs are disabled and the save action is removed so the monomer can only be viewed.
  readOnly?: boolean;
}

export default function SharedMonomerManualEditPage({
  monomerId,
  initialName,
  initialSmilesInput,
  initialCustomMolecularWeight,
  initialSymbol,
  validate,
  onSave,
  onSaveRequested = ({save}) => {
    save();
  },
  libraryUrl,
  pageContainer,
  dataTestComponent,
  readOnly = false,
}: SharedMonomerManualEditPageProps): JSX.Element {
  const [smilesInput, setSmilesInput] = useState(initialSmilesInput);
  const [canonicalSmiles, setCanonicalSmiles] = useState<string | null>(null);
  const [customMolecularWeight, setCustomMolecularWeight] = useState<string | null>(
    initialCustomMolecularWeight?.toFixed(3) ?? null
  );
  const [calculatedMolecularWeight, setCalculatedMolecularWeight] = useState<number | null>(null);
  const [editingMolecularWeight, setEditingMolecularWeight] = useState(false);
  const [attachmentPointsInput, setAttachmentPointsInput] = useState<Array<MonomerCapGroup | null> | null>(
    null
  );
  const [calculatedAttachmentPoints, setCalculatedAttachmentPoints] =
    useState<Array<MonomerCapGroupWithWildcard> | null>([]);
  const [name, setName] = useState(initialName);
  const [symbol, setSymbol] = useState(initialSymbol);
  const [structureImage, setStructureImage] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<SharedMonomerValidationErrors>({
    smiles: null,
    symbol: null,
    attachmentPoints: null,
    molecularWeight: null,
    structureImage: null,
    naturalAnalogue: null,
    customMolecularWeight: null,
    color: null,
  });
  const [isValid, setIsValid] = useState(false);
  const [editingSmiles, setEditingSmiles] = useState(true);
  const [validationInFlight, setValidationInFlight] = useState(false);
  const [saveInFlight, setSaveInFlight] = useState(false);
  const [hasCustomWeightError, setHasCustomWeightError] = useState(false);

  const hasChanges =
    name !== initialName ||
    smilesInput !== initialSmilesInput ||
    customMolecularWeight !== initialCustomMolecularWeight ||
    symbol !== initialSymbol;

  const doValidateAndSetValues = useCallback(
    async (
      monomerValidationInput: BaseMonomerValidationFields,
      syncSmilesInputWithCanonicalSmiles: boolean
    ): Promise<void> => {
      setValidationInFlight(true);
      const outputs = await validate([monomerValidationInput]);
      assert(outputs.length === 1, 'Expected exactly one monomer validation output');
      const {properties, errors, isValid} = outputs[0];
      setCanonicalSmiles(properties.canonicalSmiles);
      setCalculatedAttachmentPoints(properties.attachmentPoints);
      setAttachmentPointsInput(
        properties.attachmentPoints?.map((attachmentPoint) =>
          attachmentPoint !== WILDCARD_CAP_GROUP_SYMBOL ? attachmentPoint : null
        ) ?? null
      );
      setCalculatedMolecularWeight(properties.molecularWeight);
      setStructureImage(properties.structureImage);

      setValidationErrors(errors);
      setIsValid(isValid);
      if (syncSmilesInputWithCanonicalSmiles && properties.canonicalSmiles != null) {
        setSmilesInput(properties.canonicalSmiles);
      }
      setValidationInFlight(false);
    },
    [validate]
  );

  // Called upon first render to populate calculated values from initial SMILES
  useEffect(() => {
    const monomerValidationInput: BaseMonomerValidationFields = {
      name: initialName,
      smiles: initialSmilesInput,
      customMolecularWeight: initialCustomMolecularWeight,
      symbol: initialSymbol,
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  }, [
    initialSmilesInput,
    initialName,
    initialCustomMolecularWeight,
    initialSymbol,
    doValidateAndSetValues,
    monomerId,
  ]);

  const onSmilesInputChange = (newSmilesInput: string): void => {
    setSmilesInput(newSmilesInput);
    const monomerValidationInput: BaseMonomerValidationFields = {
      name,
      smiles: newSmilesInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  };

  const onAttachmentPointsInputChange = (
    newAttachmentPointsInput: Array<MonomerCapGroup | null> | null
  ): void => {
    const monomerValidationInput: BaseMonomerValidationFields = {
      name,
      smiles: smilesInput,
      capGroups: newAttachmentPointsInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, true);
  };

  const onNameChange = (newName: string): void => {
    setName(newName);
    const monomerValidationInput: BaseMonomerValidationFields = {
      name: newName,
      smiles: smilesInput,
      capGroups: attachmentPointsInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  };

  const onSymbolChange = (newSymbol: string): void => {
    setSymbol(newSymbol);
    const monomerValidationInput: BaseMonomerValidationFields = {
      name,
      smiles: smilesInput,
      capGroups: attachmentPointsInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol: newSymbol,
      existingMonomerId: monomerId,
    };
    doValidateAndSetValues(monomerValidationInput, false);
  };

  const saveMonomer = async (): Promise<void> => {
    assert(
      [name, symbol].every((field) => field != null),
      'Unexpected null input fields received for monomer update.'
    );
    const input: BaseMonomerUpdate = {
      name,
      smiles: smilesInput,
      customMolecularWeight: customMolecularWeight != null ? parseNumber(customMolecularWeight) : null,
      symbol,
    };

    try {
      setSaveInFlight(true);
      await onSave(input, editingSmiles);
      notifications.success('Saved monomer');
      navigateToURL(libraryUrl);
      // eslint-disable-next-line benchlint/always-throw-from-catch
    } catch (e) {
      notifications.notifyError(e);
    } finally {
      setSaveInFlight(false);
    }
  };

  const smilesInputFieldError = validationErrors.smiles ?? validationErrors.attachmentPoints;

  const smilesToggleText = readOnly ? 'View SMILES' : 'Edit SMILES';
  const cappingGroupsToggleText = readOnly ? 'View capping groups' : 'Edit capping groups';

  return (
    <div
      className={css(styles.pageContainer)}
      {...getTestProps({component: dataTestComponent, element: 'root'})}
    >
      {pageContainer}
      <div className={css(styles.middleSectionContainer)}>
        <div className={css(styles.monomerPropertiesPane)}>
          <div className={css(styles.paneContainer)}>
            <TextField
              className={css(styles.marginBottom16)}
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onNameChange(e.target.value);
              }}
              label="Name"
              labelStyle={LABEL_STYLES.LABEL}
              isRequired={true}
              autoFocus={true}
              disabled={readOnly}
              dataTestAttrs={{
                component: dataTestComponent,
                element: 'nameInput',
              }}
            />
            <TextField
              className={css(styles.marginBottom16)}
              value={symbol}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                onSymbolChange(e.target.value);
              }}
              label="Symbol"
              labelStyle={LABEL_STYLES.LABEL}
              isRequired={true}
              disabled={readOnly}
              hasError={validationErrors.symbol != null}
              error={
                validationErrors.symbol != null
                  ? {
                      message: validationErrors.symbol,
                      style: InputErrorMessageStyle.INFO,
                    }
                  : null
              }
              dataTestAttrs={{
                component: dataTestComponent,
                element: 'symbolInput',
              }}
            />
            <div className={css(styles.canonicalSmiles)}>
              <div className={css(styles.textFieldSubheader)}>
                <span>Canonical SMILES</span>
              </div>
              <div className={css(styles.grayTextFieldContainer)}>
                <span className={css(styles.grayTextField)}>{canonicalSmiles}</span>
              </div>
            </div>
            <div className={css(styles.marginBottom16)}>
              <ToggleGroup
                size={ButtonSize.MEDIUM}
                options={[smilesToggleText, cappingGroupsToggleText].map((text) => ({
                  id: text,
                  buttonProps: {text},
                }))}
                selection={editingSmiles ? smilesToggleText : cappingGroupsToggleText}
                onChangeSelection={(selection: string) => {
                  setEditingSmiles(selection === smilesToggleText);
                }}
                isFullWidth={true}
              />
            </div>
            {editingSmiles ? (
              <TextField
                className={css(styles.inputSmilesField)}
                value={smilesInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                  onSmilesInputChange(e.target.value);
                }}
                label="SMILES"
                placeholder="Enter SMILES notation"
                autoFocus={true}
                disabled={readOnly}
                hasError={smilesInputFieldError != null}
                error={
                  smilesInputFieldError != null
                    ? {
                        message: smilesInputFieldError,
                        style: InputErrorMessageStyle.INFO,
                      }
                    : null
                }
                dataTestAttrs={{
                  component: dataTestComponent,
                  element: 'smilesInput',
                }}
              />
            ) : (
              <MonomerConfigureAttachmentPointsSection
                attachmentPointsInput={attachmentPointsInput}
                onChangeAttachmentPointsInput={onAttachmentPointsInputChange}
                calculatedAttachmentPoints={calculatedAttachmentPoints}
                allowNonWildcardCapGroupSelection={true}
                disabled={readOnly}
              />
            )}
          </div>
        </div>
        <MonomerStructureAndWeightConfigurationPane
          smilesInput={smilesInput}
          customMolecularWeight={customMolecularWeight}
          calculatedMolecularWeight={calculatedMolecularWeight}
          onChangeCustomMolecularWeight={(weight: string | null) => {
            setCustomMolecularWeight(weight);
          }}
          validationErrors={validationErrors}
          editingMolecularWeight={editingMolecularWeight}
          onChangeEditingMolecularWeight={setEditingMolecularWeight}
          structureImage={structureImage}
          hasCustomWeightError={hasCustomWeightError}
          onChangeHasCustomWeightError={setHasCustomWeightError}
          readOnly={readOnly}
        />
      </div>
      <div className={css(styles.submitBar)}>
        <div className={css(styles.buttonsContainer)}>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={() => {
              navigateToURL(libraryUrl);
            }}
            size={ButtonSize.MEDIUM}
            text={readOnly ? 'Back' : 'Cancel'}
          />
          {!readOnly && (
            <Button
              buttonStyle={ButtonStyle.SECONDARY}
              text={saveInFlight ? 'Saving...' : 'Save monomer'}
              size={ButtonSize.MEDIUM}
              disabled={
                saveInFlight ||
                validationInFlight ||
                !isValid ||
                editingMolecularWeight ||
                name.length === 0 ||
                hasCustomWeightError ||
                !hasChanges
              }
              onClick={() => {
                onSaveRequested({
                  didStructureChange: smilesInput !== initialSmilesInput,
                  editingSmiles,
                  save: saveMonomer,
                });
              }}
              dataTestAttrs={{
                component: dataTestComponent,
                element: 'saveMonomerButton',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  pageContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'scroll',
  },
  middleSectionContainer: {
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
  },
  monomerPropertiesPane: {
    width: '50%',
    borderRight: `1px solid ${GRAY_LIGHT}`,
  },
  submitBar: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    padding: 16,
    gap: 10,
    boxShadow: '0px -2px 4px rgba(92, 124, 131, 0.1)',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  marginBottom16: {
    marginBottom: 16,
  },
  paneContainer: {
    marginLeft: 40,
    marginRight: 40,
    marginTop: 20,
  },
  inputSmilesField: {
    marginBottom: 12,
  },
  textFieldSubheader: {
    fontWeight: 'bold',
    marginBottom: 4,
    ...FONT_PROXIMA_13,
  },
  grayTextFieldContainer: {
    display: 'flex',
  },
  grayTextField: {
    ...FONT_PROXIMA_13,
    padding: '3px 10px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
    overflowWrap: 'anywhere',
    minHeight: 28,
    width: '100%',
  },
  canonicalSmiles: {
    paddingBottom: 16,
    marginBottom: 16,
    borderBottom: `1px solid ${GRAY}`,
  },
});
